import {
  newId,
  nowIso,
  readNotifications,
  writeNotifications,
} from 'components/views/company/outreach-template/proposals/proposal-storage';
import {
  AppNotification,
  LS_KEYS,
  NotificationRecipientRole,
} from 'components/views/company/outreach-template/proposals/types';
import { useAuthContext } from 'providers/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { subscribeTable } from 'utils/local-table';

type NewNotification = Omit<AppNotification, 'id' | 'createdAt' | 'read'>;

const matchesUser = (
  n: AppNotification,
  userSub: string,
  role: NotificationRecipientRole,
): boolean => {
  if (n.recipientUserSub) return n.recipientUserSub === userSub;
  if (n.recipientRole) return n.recipientRole === role;
  return false;
};

export const pushNotification = (n: NewNotification): void => {
  const all = readNotifications();
  const next: AppNotification = {
    ...n,
    id: newId(),
    createdAt: nowIso(),
    read: false,
  };
  writeNotifications([...all, next]);
};

export interface UseNotificationsResult {
  items: AppNotification[];
  unreadCount: number;
  markRead: (id: string) => void;
  /**
   * Mark notifications for the current user as read. Pass a refType to limit
   * to that source (e.g., only mark 'proposal' notifications) — used by the
   * bell when a filter is active so "Mark all read" respects the current view.
   */
  markAllRead: (refType?: string) => void;
  push: (n: NewNotification) => void;
}

export const useNotifications = (): UseNotificationsResult => {
  const { user, isInternal } = useAuthContext();
  const userSub = user.sub ?? '';
  const role: NotificationRecipientRole = isInternal ? 'admin' : 'client';

  const [all, setAll] = useState<AppNotification[]>(readNotifications);

  useEffect(() => subscribeTable<AppNotification>(LS_KEYS.notifications, setAll), []);

  const items = useMemo(() => {
    return all
      .filter((n) => matchesUser(n, userSub, role))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [all, userSub, role]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const markRead = useCallback((id: string) => {
    const current = readNotifications();
    writeNotifications(current.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(
    (refType?: string) => {
      const current = readNotifications();
      writeNotifications(
        current.map((n) => {
          if (!matchesUser(n, userSub, role)) return n;
          if (refType && n.refType !== refType) return n;
          return { ...n, read: true };
        }),
      );
    },
    [userSub, role],
  );

  return {
    items,
    unreadCount,
    markRead,
    markAllRead,
    push: pushNotification,
  };
};
